import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import closeGreyIcon from 'assets/images/icons/cross-grey.svg';
import recordIcon from 'assets/images/icons/records.svg';
import DropDownArrowGrey from 'assets/images/icons/dropdown-arrow-grey.svg';
import Loader from 'components/common-components/Loader';
import TableComponent from 'components/table-component/Table';
import { submissionsLogsHeader } from './utils';
import { CallAPI } from 'actions/General';
import AlertModal from 'components/common-components/AlertModal';
import SelectField from 'components/form-components/SelectField';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

function SubmissionLogs({ submissionId, setSubmissionId }) {
  const { t } = useTranslation();
  const { locale } = useSelector((state) => ({
    locale: state.language.lang,
  }));
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showMessageType, setShowMessageType] = useState('');
  const [messageDetail, setMessageDetail] = useState(null);

  const exportOptionList = [{ value: 'csv', displayValue: 'select_csv' }];

  const exportActions = async (_, value) => {
    let API = value === 'csv' ? 'EXPORT_SUBMISSION_LOGS' : '';
    const payload = { submissionId: submissionId, locale: locale };
    if (API) {
      const response = await CallAPI(API, payload, null);
      if (response.status) {
        downloadCSV(response.data);
      } else toast(t('Failed to export logs'), {
        style: { borderRadius: '8px', background: '#000', color: '#fff' },
      });
    }
  };

  const downloadCSV = (csvData) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `submission-logs_${submissionId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSubmissionLogs = async () => {
    const result = await CallAPI(
      'FETCH_SUBMISSION_LOGS',
      null,
      submissionId,
      setLoader,
      null,
      null,
    );
    if (result.status) {
      result.data.forEach((value, index) => {
        value.indexed = index + 1;
      });
      setData(result.data);
      console.log(result.data);
    }
  };

  const callbackSubmissionLogMessage = (row, type) => {
    setShowMessageType(type);
    setMessageDetail(row);
  };

  useEffect(() => {
    if (submissionId) {
      getSubmissionLogs();
    }
  }, [submissionId]);
  return (
    <>
      <div>
        <AlertModal show={true} actionButtons={[]} noPadding size='xl'>
          <div className='w-100 px-4 py-3'>
            <div className='d-flex justify-content-between align-items-center pt-1 pb-3'>
              <div className='d-flex gap-2'>
                <img src={recordIcon} />
                <label className='theme-font-inter-semi-bold theme-text-grey-1 theme-size-1_4'>
                  {t('table_header_view_logs')}
                </label>
              </div>
              <div className='d-flex gap-2'>
                <SelectField
                  label={'text_export'}
                  handleChange={exportActions}
                  listValues={exportOptionList}
                  icon={DropDownArrowGrey}
                  positionReversed
                  isAutoClose={true}
                  classes={data.length > 0 ? '' : 'pe-none'}
                />
                <img
                  src={closeGreyIcon}
                  className='cursor-pointer ps-2 py-2'
                  onClick={() => setSubmissionId('')}
                />
              </div>
            </div>
            {loader && <Loader color='green-1' type='logs_loader' />}
            <TableComponent
              header={submissionsLogsHeader}
              data={data}
              props={{
                type: 'submission_logs',
                translateValueFunc: (val) => t(val),
                callbackSubmissionLogMessage,
              }}
              isFieldSelectionEnabled={false}
              isPaginationEnabled={false}
              isActionsEnabled={false}
              filterEnabled={false}
              totalRecords={1}
              loader={false}
              maintainTableMaxHeight
            />
          </div>
        </AlertModal>
      </div>

      {messageDetail && (
        <AlertModal
          show={true}
          actionButtons={[]}
          noPadding
          backdropClassName={'backdrop-overlap'}
          classes='view-logs-message-modal'
        >
          <div className='w-100 px-4 py-3'>
            <div className='d-flex justify-content-between'>
              <span className='theme-font-inter-semi-bold theme-text-grey-1 theme-size-1_4'>
                {t('text_view_message')}
              </span>
              <img
                className='cursor-pointer ps-2 py-2'
                src={closeGreyIcon}
                onClick={() => setMessageDetail(null)}
              />
            </div>

            <div className='d-flex gap-4 mt-3'>
              <div className='d-flex flex-column align-items-start text-left'>
                <label className='theme-text-black-1'>{t('table_header_id')}</label>
                <span>{messageDetail.indexed}</span>
              </div>
              <div className='d-flex flex-column align-items-start text-left'>
                <label className='theme-text-black-1'>{t('table_header_recipient')}</label>
                <span>{messageDetail.recipient}</span>
              </div>
              <div className='d-flex flex-column align-items-start text-left'>
                <label className='theme-text-black-1'>{t('table_header_status')}</label>
                <span
                  className={`${
                    messageDetail.messageStatus === 'Sent'
                      ? 'theme-text-green-1'
                      : messageDetail.messageStatus === 'Replied'
                      ? 'theme-text-grey-2'
                      : messageDetail.messageStatus === 'Queued'
                      ? 'theme-text-orange-1'
                      : messageDetail.messageStatus === 'Undelivered'
                      ? 'theme-text-red-1'
                      : messageDetail.messageStatus === 'Delivered'
                      ? 'theme-text-blue-1'
                      : messageDetail.messageStatus === 'Read'
                      ? 'theme-text-green-1'
                      : ['report_opened', 'report_acknowledged', 'link_opened'].includes(
                          messageDetail.messageStatus,
                        )
                      ? 'theme-text-blue-3'
                      : ''
                  }`}
                >
                  {t(
                    `text_${(() => {
                      const statusKeyMap = {
                        sent: 'sent',
                        replied: 'replied',
                        queued: 'queued',
                        undelivered: 'undelivered',
                        delivered: 'delivered',
                        read: 'read',
                        link_opened: 'link_opened',
                        report_opened: 'report_opened',
                        report_acknowledged: 'report_acknowledged',
                      };

                      const status = messageDetail.messageStatus?.toLowerCase();
                      return statusKeyMap[status] || '';
                    })()}`,
                  )}
                </span>
              </div>
              <div className='d-flex flex-column align-items-start text-left'>
                <label className='theme-text-black-1'>{t('table_header_type')}</label>
                <span>{t(`text_${messageDetail.logType}`)}</span>
              </div>
              <div className='d-flex flex-column align-items-start text-left'>
                <label className='theme-text-black-1'>{t('table_header_date_and_time')}</label>
                <span>{new Date(messageDetail.createdAt).toLocaleString('en-US')}</span>
              </div>
            </div>

            <div className='comment-log-message-detail mt-3 theme-background-white-5'>
              <span className='theme-font-inter-medium'>
                {showMessageType === 'message' && messageDetail.message}{' '}
                {showMessageType === 'rejectReason' && messageDetail.rejectReason}{' '}
              </span>
            </div>
          </div>
        </AlertModal>
      )}
    </>
  );
}
SubmissionLogs.propTypes = {
  submissionId: PropTypes.string.isRequired,
  setSubmissionId: PropTypes.func.isRequired,
};
export default SubmissionLogs;
